import { useState } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "../../../theme";
import Modal from "../Modal";
import { HorizontalSpacer } from "../../atoms/Spacers";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import Checkbox from "../../atoms/inputs/Checkbox";
import TextField from "../../atoms/inputs/TextField";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import Alert from "../Alert";
import { useRejectionEmailTemplates } from "../../../graphql/hooks/contentful/useRejectionEmailTemplates";
import Select from "../../atoms/inputs/Select";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  count: number;
  onReject: (params: {
    comment: string;
    recipientEmail?: string;
    emailMessage?: string;
    sendFileAsAttachment?: boolean;
  }) => void;
  loading?: boolean;
  errorMessage?: string;
  successMessage?: string;
  canSendFileAsAttachment?: boolean;
}

const RejectInvoicesModal: React.FC<Props> = ({
  isOpen,
  onClose,
  count,
  onReject,
  loading,
  errorMessage,
  successMessage,
  canSendFileAsAttachment = false,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendFileAsAttachment, setSendFileAsAttachment] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // The form is deliberately not cleared here: closing the modal keeps the
  // draft, so reopening it restores what the user had written. The flow hook
  // remounts this component after a successful rejection to clear it.
  const handleClose = () => {
    onClose();
  };

  const handleReject = () => {
    onReject({
      comment,
      recipientEmail: sendEmail ? recipientEmail : undefined,
      emailMessage: sendEmail ? emailMessage : undefined,
      sendFileAsAttachment: sendEmail ? sendFileAsAttachment : undefined,
    });
  };

  //rejection email templates
  const { rejectionTemplates, loading: rejectionEmailTemplatesLoading } =
    useRejectionEmailTemplates();

  const getRejectionEmailOptions = (): Array<{
    value: string;
    label: string;
  }> => {
    if (!rejectionTemplates || !rejectionTemplates.items) return [];

    const templateOptions = rejectionTemplates.items.map((item) => ({
      value: item.id,
      label: item.title,
    }));

    return [{ value: "", label: "Välj mall" }, ...templateOptions];
  };

  const onSelectRejectionTemplate = (rejectionTemplateId: string) => {
    setSelectedTemplateId(rejectionTemplateId);
    setEmailMessage(
      rejectionTemplates?.items?.find((item) => item.id === rejectionTemplateId)
        ?.content || "",
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      onClose={handleClose}
      closeIcon
      width={600}
      title={t("invoiceAssessment.rejectInvoices", { count })}
      actionButtons={{
        defaultButton: {
          buttonTitle: t("common.cancel"),
          onClick: handleClose,
        },
        deleteButton: {
          buttonTitle: t("invoiceAssessment.rejectInvoices", { count }),
          onClick: handleReject,
          loading: loading,
          icon: <CrossIcon color={theme.colors.white} />,
          disabled:
            !comment ||
            (successMessage !== undefined && successMessage.length > 0),
        },
      }}
    >
      <HorizontalSpacer spacing={2} />

      <MultilineTextField
        label={t("common.comment")}
        placeholder={t("documentHandler.provideInternalReasonForRejection")}
        value={comment}
        onChange={(value) => setComment(value)}
      />
      <HorizontalSpacer spacing={1} />
      <Checkbox
        title={t("invoiceAssessment.sendAnEmailToTheIssuer")}
        checked={sendEmail}
        onChange={() => setSendEmail(!sendEmail)}
      />
      <HorizontalSpacer spacing={1} />

      <SlideOutHeight isVisible={sendEmail}>
        <div>
          {canSendFileAsAttachment && (
            <Checkbox
              title={t("documentHandler.sendDocumentAsAttachment")}
              checked={sendFileAsAttachment}
              onChange={() => setSendFileAsAttachment(!sendFileAsAttachment)}
            />
          )}
          <HorizontalSpacer spacing={1} />
          <div style={{ display: "flex", gap: 24 }}>
            <TextField
              label={t("random.recipientEmail")}
              value={recipientEmail}
              onChange={(v) => setRecipientEmail(v)}
            />
            {rejectionEmailTemplatesLoading ? (
              <SmallLoadingSpinner />
            ) : (
              <Select
                options={getRejectionEmailOptions()}
                value={selectedTemplateId}
                onChange={onSelectRejectionTemplate}
                label={t("documentHandler.selectRejectionEmailTemplate")}
              />
            )}
          </div>
          <HorizontalSpacer spacing={0.5} />
          <MultilineTextField
            label={t("random.message")}
            value={emailMessage}
            onChange={(v) => setEmailMessage(v)}
            placeholder={t("documentHandler.provideRejectionMessageToClient")}
          />
        </div>
      </SlideOutHeight>

      <HorizontalSpacer spacing={1} />
      {errorMessage && <Alert type="error" title={errorMessage} />}
      {successMessage && <Alert type="success" title={successMessage} />}
    </Modal>
  );
};

export default RejectInvoicesModal;
